<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { debounce } from '@chatwoot/utils';
import { vOnClickOutside } from '@vueuse/components';
import { useToggle } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

defineProps({
  selectedItems: {
    type: Array,
    default: () => [],
  },
  multiselectorTitle: {
    type: String,
    default: 'Selecionar Contatos',
  },
  multiselectorPlaceholder: {
    type: String,
    default: 'Buscar contatos...',
  },
  inputPlaceholder: {
    type: String,
    default: 'Buscar por nome, email ou telefone...',
  },
  icon: {
    type: String,
    default: '👥',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'remove']);
const { t } = useI18n();

const CONTACTS_PER_PAGE = 15;
const store = useStore();
const contactsFromStore = useMapGetter('contacts/getContactsList');
const meta = useMapGetter('contacts/getMeta');
const isLoadingContacts = ref(false);
const currentPage = ref(1);
const accumulatedContacts = ref([]);
const searchQuery = ref('');
const DEBOUNCE_DELAY = 500;

const [showSearchDropdown, toggleDropdown] = useToggle(false);
const buttonRef = ref(null);
const multiselectRef = ref(null);

const transformContact = contact => {
  const displayName =
    contact.name ||
    contact.email ||
    contact.phoneNumber ||
    t('CONTACT_MULTISELECT.NO_NAME');
  const searchableName = [contact.name, contact.email, contact.phoneNumber]
    .filter(Boolean)
    .join(' | ');
  return {
    id: contact.id,
    name: searchableName,
    displayName,
    email: contact.email,
    phone_number: contact.phoneNumber,
  };
};

const transformedContacts = computed(() => {
  if (accumulatedContacts.value.length > 0) {
    return accumulatedContacts.value;
  }
  if (!contactsFromStore.value || contactsFromStore.value.length === 0) {
    return [];
  }
  return contactsFromStore.value.map(transformContact);
});

const fetchContacts = async (page = 1, reset = true) => {
  isLoadingContacts.value = true;
  if (reset) {
    currentPage.value = 1;
    accumulatedContacts.value = [];
  }
  try {
    await store.dispatch('contacts/clearContactFilters');
    await store.dispatch('contacts/get', {
      page,
      sortAttr: 'name',
    });
    await nextTick();
    if (contactsFromStore.value?.length > 0) {
      const newContacts = contactsFromStore.value.map(transformContact);
      if (reset) {
        accumulatedContacts.value = newContacts;
      } else {
        const existingIds = new Set(accumulatedContacts.value.map(c => c.id));
        const unique = newContacts.filter(c => !existingIds.has(c.id));
        accumulatedContacts.value = [...accumulatedContacts.value, ...unique];
      }
    }
    currentPage.value = page;
  } catch {
    // Error loading contacts
  } finally {
    isLoadingContacts.value = false;
  }
};

const searchContactsPage = async (query, page = 1) => {
  if (!query || query.trim() === '') {
    return;
  }

  isLoadingContacts.value = true;
  const reset = page === 1;
  if (reset) {
    currentPage.value = 1;
    accumulatedContacts.value = [];
  }
  try {
    await store.dispatch('contacts/clearContactFilters');
    await store.dispatch('contacts/search', {
      page,
      sortAttr: 'name',
      search: encodeURIComponent(query.trim()),
    });
    await nextTick();
    if (contactsFromStore.value?.length > 0) {
      const newContacts = contactsFromStore.value.map(transformContact);
      if (reset) {
        accumulatedContacts.value = newContacts;
      } else {
        const existingIds = new Set(accumulatedContacts.value.map(c => c.id));
        const unique = newContacts.filter(c => !existingIds.has(c.id));
        accumulatedContacts.value = [...accumulatedContacts.value, ...unique];
      }
    }
    currentPage.value = page;
  } catch {
    // Error searching contacts
  } finally {
    isLoadingContacts.value = false;
  }
};

const searchContacts = debounce((query, page = 1) => {
  searchContactsPage(query, page);
}, DEBOUNCE_DELAY);

const loadMoreContacts = async () => {
  if (isLoadingContacts.value) return;

  const nextPage = currentPage.value + 1;
  const totalPages = meta.value?.count
    ? Math.ceil(meta.value.count / (meta.value.per_page || CONTACTS_PER_PAGE))
    : 1;

  if (nextPage > totalPages) return;

  const query = searchQuery.value?.trim() || '';
  if (query) {
    await searchContactsPage(query, nextPage);
  } else {
    await fetchContacts(nextPage, false);
  }
};

const handleScroll = event => {
  if (event?.nearBottom) {
    loadMoreContacts();
  }
};

const handleContactSelect = contact => {
  emit('select', contact);
};

const handleContactRemove = contact => {
  emit('remove', contact);
};

const loadInitialContacts = async () => {
  await fetchContacts(1, true);
};

watch(showSearchDropdown, async isVisible => {
  if (isVisible) {
    await nextTick();
    setTimeout(() => {
      const searchInput = document.querySelector('.search-input');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }
});

const onCloseDropdown = () => {
  toggleDropdown(false);
};

watch(searchQuery, newValue => {
  if (!newValue || newValue.trim() === '') {
    fetchContacts(1, true);
    return;
  }
  searchContacts(newValue, 1);
});

watch(
  () => multiselectRef.value?.search,
  newSearch => {
    if (newSearch !== undefined) {
      searchQuery.value = newSearch;
    }
  },
  { deep: true }
);

defineExpose({
  loadInitialContacts,
});
</script>

<template>
  <div class="w-full">
    <!-- Botão e Dropdown -->
    <div
      ref="buttonRef"
      v-on-click-outside="onCloseDropdown"
      class="relative w-full"
      @keyup.esc="onCloseDropdown"
    >
      <Button
        slate
        outline
        trailing-icon
        :icon="
          showSearchDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
        "
        class="w-full !px-3 !py-2 h-8 bg-n-alpha-black2 [&:not(.focused)]:!outline-transparent"
        :disabled="disabled"
        @click="() => toggleDropdown()"
      >
        <div class="flex items-center justify-between w-full min-w-0">
          <div class="flex items-center space-x-2">
            <span class="text-sm">{{ icon }}</span>
            <h4 class="text-sm text-ellipsis text-n-slate-12">
              {{ multiselectorPlaceholder }}
            </h4>
            <span
              v-if="selectedItems.length > 0"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
            >
              {{ selectedItems.length }}
            </span>
          </div>
        </div>
      </Button>

      <!-- Dropdown positioned relative to button -->
      <div
        v-if="showSearchDropdown"
        class="absolute top-full left-0 w-full min-w-[220px] height-[220px] overflow-y-auto mt-1 z-[999999] border rounded-lg bg-n-alpha-black2 backdrop-blur-[100px] shadow-lg border-n-strong dark:border-n-strong p-3"
      >
        <div class="flex items-center justify-between mb-1">
          <h4
            class="m-0 overflow-hidden text-sm text-n-slate-11 whitespace-nowrap text-ellipsis"
          >
            {{ multiselectorTitle }}
          </h4>
          <Button ghost slate xs icon="i-lucide-x" @click="onCloseDropdown" />
        </div>

        <MultiselectDropdownItems
          ref="multiselectRef"
          :options="transformedContacts"
          :selected-items="selectedItems"
          :input-placeholder="inputPlaceholder"
          no-search-result="Nenhum contato encontrado"
          :has-thumbnail="false"
          @select="handleContactSelect"
          @scroll="handleScroll"
        />
      </div>
    </div>

    <!-- Contatos Selecionados -->
    <div class="mt-3 space-y-2">
      <div class="flex items-center justify-between">
        <h5 class="text-sm font-medium text-n-slate-11">
          {{
            t('CONTACT_MULTISELECT.SELECTED_COUNT', {
              count: selectedItems.length,
            })
          }}
        </h5>
      </div>

      <div
        class="flex flex-col space-y-2 min-h-[12rem] max-h-48 overflow-y-auto"
      >
        <!-- Estado vazio quando não há contatos selecionados -->
        <div
          v-if="selectedItems.length === 0"
          class="flex items-center justify-center flex-1 p-4 bg-n-alpha-2 dark:bg-n-solid-2 rounded-lg border border-n-strong border-dashed"
        >
          <div class="text-center">
            <div
              class="w-8 h-8 bg-n-slate-4 dark:bg-n-slate-6 rounded-full flex items-center justify-center mx-auto mb-2"
            >
              <span class="text-n-slate-8 dark:text-n-slate-10 text-sm">
                {{ t('CONTACT_MULTISELECT.EMPTY_ICON') }}
              </span>
            </div>
            <p class="text-sm text-n-slate-10 dark:text-n-slate-9">
              {{ t('CONTACT_MULTISELECT.NO_CONTACT_SELECTED') }}
            </p>
            <p class="text-xs text-n-slate-9 dark:text-n-slate-8 mt-1">
              {{ t('CONTACT_MULTISELECT.SELECT_USING_BUTTON') }}
            </p>
          </div>
        </div>

        <!-- Lista de contatos selecionados -->
        <div
          v-for="contact in selectedItems"
          :key="contact.id"
          class="flex items-center justify-between p-2 bg-n-alpha-2 dark:bg-n-solid-2 rounded-lg border border-n-strong"
        >
          <div class="flex items-center space-x-3 min-w-0 flex-1">
            <div class="flex-shrink-0">
              <div
                class="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center"
              >
                <span
                  class="text-xs font-medium text-blue-600 dark:text-blue-400"
                >
                  {{
                    contact.displayName?.charAt(0)?.toUpperCase() ||
                    t('CONTACT_MULTISELECT.INITIAL_FALLBACK')
                  }}
                </span>
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{
                  contact.displayName ||
                  contact.name ||
                  t('CONTACT_MULTISELECT.NO_NAME')
                }}
              </p>
              <div class="flex items-center space-x-2 text-xs text-n-slate-10">
                <span v-if="contact.email" class="truncate">{{
                  contact.email
                }}</span>
                <span v-if="contact.phone_number" class="truncate">{{
                  contact.phone_number
                }}</span>
              </div>
            </div>
          </div>
          <Button
            ghost
            slate
            xs
            icon="i-lucide-x"
            class="flex-shrink-0"
            @click="handleContactRemove(contact)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
